<script setup>
import { ref, onMounted, computed, watch, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { reservarZIndex, liberarZIndex, BASE_ZINDEX } from '@/composables/useZIndex'
import { useMetadataListasStore } from '@/stores/metadataListas'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import ComexPipelineStatus from '@/components/base/fluxo/ComexPipelineStatus.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import DocumentoEntradaGrid from '@/components/Compras/documentoEntrada/DocumentoEntradaGrid.vue'
import SolicitacaoAdiantamentoGrid from '@/components/Financeiro/contasPagarReceber/solicitacaoAdiantamento/SolicitacaoAdiantamentoGrid.vue'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  contextoPai: {
    type: Object,
    default: () => ({})
  },
  drilldownMode: {
    type: Boolean,
    default: false
  },
  pipelineProps: {
    type: Object,
    default: () => ({})
  },
  funcao: {
    type: String,
    default: 'edicao',
    validator: (v) => ['edicao', 'adicao'].includes(v)
  }
})

const emit = defineEmits([
  'itemAtualizado',
  'cancelar',
  'show-notification',
  'drilldown-pop',
  'pipeline-navegar',
  'drilldown-push',
  'update:funcao'
])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

// ============================================================
// ESTADO
// ============================================================
const isLoading = ref(false)
const idEmbarque = computed(() => props.pipelineProps?.idEmbarque || props.itemParaEditar?.idEmbarque)
const numeroProcessoEmbarque = computed(() => props.itemParaEditar?.numeroProcessoEmbarque || props.pipelineProps?.numeroProcessoEmbarque)
const lstStatusEmbarque = computed(() => props.itemParaEditar?.lstStatusEmbarque || '')

const dadosValidacao = ref(null)
const listas = ref({})

// ============================================================
// MODAL LANÇAR DESPESAS
// ============================================================
const showModalDocumentosGrid = ref(false)
const documentoSelecionado = ref(null)
const documentoEntradaGridRef = ref(null)

// ============================================================
// ESTADO PARA SOLICITAÇÕES DE ADIANTAMENTO
// ============================================================
const showModalSolicitacaoAdiantamento = ref(false)
const solicitacaoAdiantamentoGridRef = ref(null)

// ============================================================
// MODAL DE ASSOCIAÇÃO LPCO
// ============================================================
const showAssociarModal = ref(false)

// --- Empilhamento (z-index) via registry central ---
const tokenZIndex = ref(null)
const zIndexReservado = ref(null)
const zIndexEfetivo = computed(() => zIndexReservado.value ?? BASE_ZINDEX)

watch(showAssociarModal, (aberto) => {
  if (aberto) {
    const { token, zIndex: z } = reservarZIndex()
    tokenZIndex.value = token
    zIndexReservado.value = z
  } else {
    liberarZIndex(tokenZIndex.value)
    tokenZIndex.value = null
    zIndexReservado.value = null
  }
})

onUnmounted(() => {
  liberarZIndex(tokenZIndex.value)
  tokenZIndex.value = null
  zIndexReservado.value = null
})

const associarData = ref({
  idEmbarqueItem: null,
  lpcoSelecionado: null,
  quantidade: 0,
  saldoMaximo: 0
})

// ============================================================
// COMPUTADOS
// ============================================================
const resumoValidacao = computed(() => {
  const itens = dadosValidacao.value?.itens || []
  return {
    totalItens: itens.length,
    itensOK: itens.filter(i => i.statusOk).length,
    itensPendentes: itens.filter(i => i.temExigencia && !i.statusOk).length,
    itensSemExigencia: itens.filter(i => !i.temExigencia).length
  }
})

const getTextoLista = (lista, id) => {
  if (!lista) return id || '-'
  const found = lista.find(item => String(item.idListaClienteItem) === String(id) || String(item.idListaSistemaItem) === String(id))
  return found ? found.textoLista : (id || '-')
}

const formatarNumero = (val, decimals = 4) => {
  if (val === null || val === undefined || val === '') return '0'
  const num = Number(val)
  if (isNaN(num)) return '0'
  return num.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals
  })
}

const calcularCobertura = (item) => {
  if (!item || !item.quantidadeEmbarcada) return 0
  const vinculado = item.vinculosAtuais?.reduce((acc, v) => acc + Number(v.quantidadeUtilizada || 0), 0) || 0
  return Math.min(100, Math.round((vinculado / item.quantidadeEmbarcada) * 100))
}

// ============================================================
// CARREGAR DADOS
// ============================================================
const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas([
      'Embarque',
      'EmbarqueItem',
      'Produto',
      'LpcoComex',
      'LpcoComexItem'
    ])
    listas.value = result
  } catch (e) {
    console.error('Erro ao carregar listas:', e)
  }
}

const carregarValidacao = async () => {
  if (!idEmbarque.value) return
  isLoading.value = true
  try {
    // Define o momento da validação: 'pre' (pré-embarque) ou 'pos' (pós-embarque)
    // O valor vem via pipelineProps, com fallback para 'pre'
    const momento = props.pipelineProps?.momentoValidacao || 'pre'
    const res = await api.get(`/Embarques/${idEmbarque.value}/validacao-lpco`, {
      params: { momento }
    })
    dadosValidacao.value = res.data
  } catch (e) {
    console.error('Erro ao carregar dados de validação:', e)
    emit('show-notification', { message: 'Erro ao carregar dados de validação LPCO.', type: 'error' })
  } finally {
    isLoading.value = false
  }
}

// ============================================================
// ASSOCIAR / DESASSOCIAR LPCO
// ============================================================
const abrirModalAssociar = (item) => {
  const lpco = item.lpcosDisponiveis?.[0]
  if (!lpco) {
    emit('show-notification', { message: 'Nenhuma LPCO disponível para este item.', type: 'warning' })
    return
  }
  associarData.value = {
    idEmbarqueItem: item.idEmbarqueItem,
    lpcoSelecionado: lpco,
    quantidade: Math.min(lpco.saldoDisponivel, item.quantidadeEmbarcada - (item.vinculosAtuais?.reduce((s, v) => s + Number(v.quantidadeUtilizada || 0), 0) || 0)),
    saldoMaximo: lpco.saldoDisponivel
  }
  showAssociarModal.value = true
}

const confirmarAssociacao = async () => {
  if (!associarData.value.lpcoSelecionado || associarData.value.quantidade <= 0) return
  try {
    await api.post(`/Embarques/${idEmbarque.value}/validacao-lpco/associar`, {
      idEmbarqueItem: associarData.value.idEmbarqueItem,
      idLpcoComexItem: associarData.value.lpcoSelecionado.idLpcoComexItem,
      quantidade: associarData.value.quantidade
    })
    emit('show-notification', { message: 'LPCO associada com sucesso!', type: 'success' })
    showAssociarModal.value = false
    await carregarValidacao()
  } catch (e) {
    emit('show-notification', { message: 'Erro ao associar LPCO.', type: 'error' })
  }
}

const removerVinculo = async (vinculo) => {
  try {
    await api.post(`/Embarques/${idEmbarque.value}/validacao-lpco/desassociar`, {
      idEmbarqueItemLpcoVinculo: vinculo.idEmbarqueItemLpcoVinculo
    })
    emit('show-notification', { message: 'LPCO desassociada com sucesso!', type: 'success' })
    await carregarValidacao()
  } catch (e) {
    emit('show-notification', { message: 'Erro ao desassociar LPCO.', type: 'error' })
  }
}

const trocarLpco = async (item, vinculoAtual) => {
  // Remove o vínculo atual e abre o modal para selecionar nova LPCO
  await removerVinculo(vinculoAtual)
  abrirModalAssociar(item)
}

// ============================================================
// HANDLERS DE NAVEGAÇÃO
// ============================================================
const isLoadingConcluirValidacao = ref(false)
const isLoadingRetornarEtapa = ref(false)

const handlePipelineClick = (etapa) => {
  if (etapa.key === 'materiais') {
    emit('pipeline-navegar', etapa.key)
  } else if (etapa.key === 'embarque') {
    emit('drilldown-push', {
      type: 'embarque',
      id: idEmbarque.value,
      props: {
        itemParaEditar: props.itemParaEditar,
        contextoPai: props.contextoPai
      }
    })
  } else {
    emit('pipeline-navegar', etapa.key)
  }
}

const handleCancel = () => {
  emit('drilldown-pop')
}

// ============================================================
// CONCLUIR VALIDAÇÃO LPCO PRÉ
// ============================================================
const concluirValidacaoLPCO = async () => {
  if (!idEmbarque.value) return

  // Validação local: verifica se há itens pendentes
  if (resumoValidacao.value.itensPendentes > 0) {
    emit('show-notification', {
      message: `Não é possível concluir a validação LPCO Pré: existem ${resumoValidacao.value.itensPendentes} item(ns) com pendências. Associe LPCOs a todos os itens com exigência antes de concluir.`,
      type: 'warning'
    })
    return
  }

  isLoadingConcluirValidacao.value = true
  try {
    // 1. Salvar os dados do formulário primeiro (PUT) para persistir no banco
    //    antes de alterar o status (mesmo padrão do iniciarTransito)
    if (props.itemParaEditar?.idEmbarque) {
      const payload = { ...props.itemParaEditar }
      await api.put(`/Embarques/${idEmbarque.value}`, payload)
    }

    // 2. Chamar API de transição de status
    const res = await api.post(`/Embarques/${idEmbarque.value}/concluir-validacao-lpco-pre`)
    const embarqueAtualizado = res.data
    emit('show-notification', { message: 'Validação LPCO Pré concluída! Status alterado para Pré-embarque.', type: 'success' })
    emit('itemAtualizado', embarqueAtualizado)

    // 3. Navegar para a próxima etapa da pipeline (embarque completo)
    emit('drilldown-push', {
      type: 'embarque',
      id: idEmbarque.value,
      props: {
        itemParaEditar: embarqueAtualizado,
        contextoPai: props.contextoPai
      }
    })
  } catch (e) {
    const data = e.response?.data
    let msg = ''
    if (typeof data === 'string') {
      msg = data
    } else if (data?.message) {
      msg = data.message
    } else if (data?.title) {
      msg = data.title
    } else if (e.response?.status === 400) {
      msg = 'Não é possível concluir: existem pendências de LPCO no embarque. Verifique todos os itens.'
    } else {
      msg = 'Erro ao concluir validação LPCO. Tente novamente.'
    }
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingConcluirValidacao.value = false
  }
}

const retornarEtapaAnterior = async () => {
  if (!idEmbarque.value || isLoadingRetornarEtapa.value) return

  isLoadingRetornarEtapa.value = true
  try {
    const res = await api.post(`/Embarques/${idEmbarque.value}/retornar-etapa-anterior`)
    const embarqueAtualizado = res.data
    // Garantir que lstStatusEmbarque seja propagado no objeto emitido
    if (embarqueAtualizado && !embarqueAtualizado.lstStatusEmbarque) {
      embarqueAtualizado.lstStatusEmbarque = props.itemParaEditar?.lstStatusEmbarque
    }
    emit('show-notification', {
      message: 'Embarque retornado para a etapa anterior com sucesso.',
      type: 'success'
    })
    emit('pipeline-navegar', 'materiais')
    emit('itemAtualizado', embarqueAtualizado)
  } catch (e) {
    const data = e.response?.data
    const msg = typeof data === 'string' ? data : (data?.message || 'Erro ao retornar etapa anterior.')
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingRetornarEtapa.value = false
  }
}

onMounted(async () => {
  await carregarListas()
  await carregarValidacao()
})
</script>

<template>
  <div class="validacao-lpco-form-wrapper">
    <FormularioCadastro
      module-name="Embarque"
      :is-loading="isLoading"
      :is-editing="!!idEmbarque"
      :entity-info="{ id: numeroProcessoEmbarque || idEmbarque, name: numeroProcessoEmbarque || 'Nova', nameLabel: 'Nº Processo' }"
      :status="getTextoLista(listas.LstStatusEmbarque, lstStatusEmbarque)"
      :audit-info="props.itemParaEditar"
      :ocultar-salvar="true"
      @cancelar="handleCancel"
      @show-notification="(p) => emit('show-notification', p)"
    >
      <template #info-header>
        <div class="info-item">
          <strong>Nº Doc. Transporte: </strong>
          <span>{{ getTextoLista(listas.LstTipoDocumentoTransporte, props.itemParaEditar?.lstTipoDocumentoTransporte) }} {{ itemsParaEditar?.numeroDocumentoTransporte || '---' }}</span>
        </div>
        <div class="info-item">
          <strong>Operação Comex: </strong>
          <span>{{ getTextoLista(listas.LstTipoOperacaoComex, props.itemParaEditar?.lstTipoOperacaoComex) || '---' }}</span>
        </div>
      </template>

      <!-- PIPELINE -->
      <ComexPipelineStatus
        v-if="drilldownMode"
        v-bind="pipelineProps"
        :idEmbarque="idEmbarque"
        :lstStatusEmbarque="lstStatusEmbarque"
        @navegar="handlePipelineClick"
      />

      <!-- BARRA DE AÇÕES: Lançar Despesas + Concluir Validação + Cancelar Embarque -->
      <!-- Oculta em modo read-only (etapa ultrapassada selecionada para visualização) -->
      <div v-if="drilldownMode && idEmbarque && !pipelineProps?.readOnly" class="d-flex justify-content-between align-items-center gap-2 mb-3">
        <div class="d-flex align-items-center gap-2">
          <button type="button" class="btn btn-sm btn-outline-warning fw-bold" @click="showModalDocumentosGrid = true">
            <i class="bi bi-receipt me-1"></i> Lançar Despesas
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-info fw-bold"
            @click="showModalSolicitacaoAdiantamento = true"
          >
            <i class="bi bi-cash-coin me-1"></i> Solicitar Adiantamento
          </button>
        </div>
        <div class="d-flex align-items-center gap-2">
          <button type="button" class="btn btn-sm btn-outline-danger fw-bold" @click="handleCancel">
            <i class="bi bi-x-lg me-1"></i> Cancelar Embarque
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary fw-bold"
            :disabled="isLoadingRetornarEtapa"
            @click="retornarEtapaAnterior"
          >
            <span v-if="isLoadingRetornarEtapa" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-arrow-return-left me-1"></i>
            Retornar Etapa Anterior
          </button>
          <button
            type="button"
            class="btn btn-sm btn-success fw-bold text-white"
            :disabled="isLoadingConcluirValidacao"
            @click="concluirValidacaoLPCO"
          >
            <span v-if="isLoadingConcluirValidacao" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-check2-circle me-1"></i>
            Concluir Validação LPCO Pré
          </button>
        </div>
      </div>

      <!-- RESUMO DE VALIDAÇÃO -->
      <div class="row g-3 mb-3">
        <div class="col-md-3">
          <div class="card border-primary-subtle bg-primary bg-opacity-10 h-100">
            <div class="card-body text-center py-3">
              <i class="bi bi-box-seam text-primary fs-2 mb-2"></i>
              <h6 class="mb-0">Total Itens</h6>
              <span class="fs-4 fw-bold text-primary">{{ resumoValidacao.totalItens }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card border-success-subtle bg-success bg-opacity-10 h-100">
            <div class="card-body text-center py-3">
              <i class="bi bi-check-circle-fill text-success fs-2 mb-2"></i>
              <h6 class="mb-0">Itens OK</h6>
              <span class="fs-4 fw-bold text-success">{{ resumoValidacao.itensOK }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card border-warning-subtle bg-warning bg-opacity-10 h-100">
            <div class="card-body text-center py-3">
              <i class="bi bi-exclamation-triangle-fill text-warning fs-2 mb-2"></i>
              <h6 class="mb-0">Pendentes</h6>
              <span class="fs-4 fw-bold text-warning">{{ resumoValidacao.itensPendentes }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card border-secondary-subtle bg-secondary bg-opacity-10 h-100">
            <div class="card-body text-center py-3">
              <i class="bi bi-x-circle-fill text-secondary fs-2 mb-2"></i>
              <h6 class="mb-0">Sem Exigência</h6>
              <span class="fs-4 fw-bold text-secondary">{{ resumoValidacao.itensSemExigencia }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- LISTA DE ITENS -->
      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-warning me-2"></div>
        Carregando dados de validação...
      </div>

      <div v-else-if="!dadosValidacao?.itens?.length" class="text-center py-5 text-muted">
        <i class="bi bi-inboxes fs-1 d-block mb-3"></i>
        <p class="mb-0">Nenhum item encontrado neste embarque.</p>
      </div>

      <div v-else class="validacao-itens-grid">
        <div
          v-for="item in dadosValidacao.itens"
          :key="item.idEmbarqueItem"
          class="card mb-3 validacao-item-card"
          :class="{
            'card-ok': item.statusOk,
            'card-pendente': !item.statusOk && item.temExigencia,
            'card-sem-exigencia': !item.temExigencia
          }"
        >
          <!-- HEADER DO ITEM -->
          <div class="card-header d-flex justify-content-between align-items-center"
               :class="{
                 'bg-success-subtle': item.statusOk,
                 'bg-warning-subtle': !item.statusOk && item.temExigencia,
                 'bg-secondary-subtle': !item.temExigencia
               }">
            <div class="d-flex align-items-center gap-2">
              <i class="bi bi-box-seam fs-5"></i>
              <div>
                <strong class="d-block">{{ item.nomeProduto }}</strong>
                <small class="text-muted">
                  <code v-if="item.codigoProduto">{{ item.codigoProduto }} </code>
                  Qtd: {{ formatarNumero(item.quantidadeEmbarcada) }} {{ getTextoLista(listas.LstUnidadeMedidaBasica, item.lstUnidadeMedida) }}
                </small>
              </div>
            </div>
            <div class="d-flex align-items-center gap-2">
              <!-- Badge de exigência -->
              <span v-if="item.temExigencia" class="badge bg-warning bg-opacity-25 text-warning-emphasis border border-warning-subtle">
                <i class="bi bi-shield-check me-1"></i>Exige LPCO
              </span>
              <span v-else class="badge bg-secondary bg-opacity-25 text-secondary border border-secondary-subtle">
                <i class="bi bi-check-circle me-1"></i>Sem Exigência
              </span>
              <!-- Badge de status -->
              <span v-if="item.statusOk" class="badge bg-success text-white">
                <i class="bi bi-check-circle me-1"></i>OK
              </span>
              <span v-else-if="item.temExigencia" class="badge bg-danger text-white">
                <i class="bi bi-exclamation-triangle me-1"></i>Pendente
              </span>
            </div>
          </div>

          <!-- CORPO DO CARD -->
          <div class="card-body">
            <!-- Exigências -->
            <div v-if="item.exigencias?.length" class="mb-2">
              <span class="badge bg-info bg-opacity-10 text-info-emphasis border border-info-subtle me-1 mb-1 py-2"
                    v-for="ex in item.exigencias" :key="ex.idProdutoExigenciasLicenca">
                <i class="bi bi-building me-1"></i>{{ getTextoLista(listas.LstOrgaoAnuente, ex.lstOrgaoAnuente) || ex.lstOrgaoAnuente }}
                <span v-if="ex.lstModeloLPCO" class="ms-2 badge bg-light text-dark border border-secondary-subtle" style="font-size:0.75rem;">
                  Modelo LPCO: {{ getTextoLista(listas.LstModeloLPCO, ex.lstModeloLPCO) || ex.lstModeloLPCO }}
                </span>
              </span>
            </div>

            <!-- LPCOs Associadas -->
            <div v-if="item.vinculosAtuais?.length" class="mb-3">
              <div class="fw-bold small text-muted mb-1">
                <i class="bi bi-link-45deg me-1"></i>LPCOs Associadas:
              </div>
              <div v-for="v in item.vinculosAtuais" :key="v.idEmbarqueItemLpcoVinculo"
                   class="vinculo-row d-flex justify-content-between align-items-center p-2 mb-1 rounded border">
                <div class="d-flex align-items-center gap-2">
                  <i class="bi bi-file-earmark-text" :class="v.deferido ? 'text-success' : 'text-warning'"></i>
                  <div>
                    <strong>{{ v.numeroLPCO || '#' + v.idLpcoComexItem }}</strong>
                    <span v-if="v.lstModeloLPCO" class="badge bg-light text-dark border border-secondary-subtle ms-1" style="font-size:0.75rem;">
                      Modelo LPCO: {{ getTextoLista(listas.LstModeloLPCO, v.lstModeloLPCO) || v.lstModeloLPCO }}
                    </span>
                    <small class="text-muted d-block">
                      {{ getTextoLista(listas.LstOrgaoAnuente, v.lstOrgaoAnuente) || v.lstOrgaoAnuente }} |
                      Utilizando: {{ formatarNumero(v.quantidadeUtilizada) }} |
                      Saldo LPCO: {{ formatarNumero(v.saldoRestanteLpco) }}
                      <span v-if="v.deferido" class="text-success ms-2">✅ Deferido</span>
                      <span v-else class="text-warning ms-2">⏳ Aguardando</span>
                    </small>
                  </div>
                </div>
                <div class="d-flex gap-1" v-if="!pipelineProps?.readOnly">
                  <button type="button" class="btn btn-sm btn-outline-warning"
                          title="Trocar LPCO"
                          @click="trocarLpco(item, v)">
                    <i class="bi bi-arrow-repeat"></i>
                  </button>
                  <button type="button" class="btn btn-sm btn-outline-danger"
                          title="Remover LPCO"
                          @click="removerVinculo(v)">
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- Alerta de falta de LPCO -->
            <div v-if="item.temExigencia && !item.lpcosDisponiveis?.length && !item.vinculosAtuais?.length"
                 class="alert alert-danger py-2 mb-2">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              <strong>Nenhuma LPCO disponível</strong> para este produto. Cadastre uma LPCO primeiro.
            </div>

            <!-- Barra de cobertura -->
            <div v-if="item.temExigencia" class="mb-2">
              <div class="d-flex justify-content-between small mb-1">
                <span>Cobertura:</span>
                <span class="fw-bold">{{ formatarNumero(item.vinculosAtuais?.reduce((s, v) => s + Number(v.quantidadeUtilizada || 0), 0) || 0) }}
                  / {{ formatarNumero(item.quantidadeEmbarcada) }}</span>
              </div>
              <div class="progress" style="height: 8px;">
                <div class="progress-bar"
                     :class="calcularCobertura(item) >= 100 ? 'bg-success' : 'bg-warning'"
                     :style="{ width: calcularCobertura(item) + '%' }">
                </div>
              </div>
            </div>

            <!-- Botão Adicionar LPCO -->
            <div v-if="!pipelineProps?.readOnly && item.temExigencia && item.lpcosDisponiveis?.length" class="text-center mt-2">
              <button type="button" class="btn btn-sm btn-outline-primary fw-bold"
                      @click="abrirModalAssociar(item)">
                <i class="bi bi-plus-circle me-1"></i>
                Adicionar LPCO
                <span class="badge bg-primary ms-1">{{ item.lpcosDisponiveis.length }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

    </FormularioCadastro>

    <!-- MODAL: Lançar Despesas -->
    <ModalFormulario
      v-model="showModalDocumentosGrid"
      titulo="Lançar Despesas"
      :ocultar-confirmar="true"
      tamanho="xxl"
      @update:model-value="showModalDocumentosGrid = $event"
    >
      <DocumentoEntradaGrid
        ref="documentoEntradaGridRef"
        :id-embarque="idEmbarque"
        v-model:documento-selecionado="documentoSelecionado"
        :tem-modulo-documento="true"
        :tem-completo="true"
        :tem-visualizacao="true"
        :pode-editar="true"
        :listas="listas"
        :exibir-primeiro-produto="true"
        :id-organizacao="authStore.empresaAtiva"
        :com-smart-mode="true"
        :id-parceiro-negocio-fornecedor="contextoPai?.idParceiroNegocioFornecedor || null"
        contexto-origem="embarque"
        modo-alocacao="livre"
        @show-notification="(p) => emit('show-notification', p)"
      />
    </ModalFormulario>

    <!-- MODAL: Solicitar Adiantamento -->
    <ModalFormulario
      v-model="showModalSolicitacaoAdiantamento"
      titulo="Solicitações de Adiantamento"
      :ocultar-confirmar="true"
      tamanho="xl"
      @update:model-value="showModalSolicitacaoAdiantamento = $event"
    >
      <SolicitacaoAdiantamentoGrid
        ref="solicitacaoAdiantamentoGridRef"
        :id-embarque="idEmbarque"
        :pode-editar="true"
        :pode-visualizar="true"
        :id-organizacao="authStore.empresaAtiva"
        @show-notification="(p) => emit('show-notification', p)"
      />
    </ModalFormulario>

    <!-- MODAL: Associar LPCO -->
    <Teleport to="body" v-if="showAssociarModal">
      <div class="modal-overlay" :style="{ zIndex: zIndexEfetivo }" @click.self="showAssociarModal = false">
        <div class="modal-card" style="max-width: 500px;">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-link-45deg me-2"></i>
              Associar LPCO ao Item
            </h5>
            <button type="button" class="btn-close" @click="showAssociarModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label fw-bold">LPCO</label>
              <select class="form-select" v-model="associarData.lpcoSelecionado">
                <option value="" disabled>Selecione uma LPCO...</option>
                <option v-for="lpco in (dadosValidacao?.itens?.find(i => i.idEmbarqueItem === associarData.idEmbarqueItem)?.lpcosDisponiveis || [])"
                        :key="lpco.idLpcoComexItem"
                        :value="lpco">
                  {{ lpco.numeroLPCO || '#' + lpco.idLpcoComexItem }}
                  ({{ getTextoLista(listas.LstOrgaoAnuente, lpco.lstOrgaoAnuente) || lpco.lstOrgaoAnuente }}
                  | {{ getTextoLista(listas.LstModeloLPCO, lpco.lstModeloLPCO) || lpco.lstModeloLPCO || 'S/ Modelo' }}
                  | Saldo: {{ formatarNumero(lpco.saldoDisponivel) }} {{ getTextoLista(listas.LstUnidadeMedidaComex, lpco.lstUnidadeMedidaComex) || lpco.lstUnidadeMedidaComex }})
                </option>
              </select>
            </div>
            <div class="mb-3" v-if="associarData.lpcoSelecionado">
              <label class="form-label fw-bold">Quantidade a Utilizar</label>
              <input type="number" class="form-control"
                     v-model.number="associarData.quantidade"
                     :min="0" :max="associarData.saldoMaximo" step="0.01" />
              <small class="text-muted">
                Máximo disponível: {{ formatarNumero(associarData.saldoMaximo) }}
                {{ associarData.lpcoSelecionado.lstUnidadeMedidaComex }}
              </small>
              <!-- Slider -->
              <input type="range" class="form-range mt-2"
                     v-model.number="associarData.quantidade"
                     :min="0" :max="associarData.saldoMaximo" step="0.1" />
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" @click="showAssociarModal = false">
              Cancelar
            </button>
            <button type="button" class="btn btn-primary fw-bold"
                    :disabled="!associarData.lpcoSelecionado || associarData.quantidade <= 0"
                    @click="confirmarAssociacao">
              <i class="bi bi-check-circle me-1"></i>
              Confirmar Associação
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.validacao-lpco-form-wrapper {
  max-width: 100%;
  overflow-x: hidden;
}

.validacao-itens-grid {
  display: grid;
  gap: 16px;
}

.validacao-item-card {
  border-left: 4px solid var(--bs-secondary);
  transition: box-shadow 0.2s;
}

.validacao-item-card:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.validacao-item-card.card-ok {
  border-left-color: var(--bs-success);
}

.validacao-item-card.card-pendente {
  border-left-color: var(--bs-warning);
}

.validacao-item-card.card-sem-exigencia {
  border-left-color: var(--bs-secondary);
}

.vinculo-row {
  background: var(--bs-tertiary-bg);
}

/* ============================================================ */
/* MODAL OVERLAY (Teleport) */
/* ============================================================ */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-card {
  background: var(--bs-body-bg);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--bs-border-color);
}

.modal-header .modal-title {
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid var(--bs-border-color);
}

/* Responsivo */
@media (min-width: 768px) {
  .validacao-itens-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (min-width: 1200px) {
  .validacao-itens-grid {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
